// src/features/dashboard/components/DashboardMetrics.tsx

import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import { DashboardSummaryResponse } from '../types';

interface DashboardMetricsProps {
  data: DashboardSummaryResponse;
}

interface MetricCardProps {
  value: string | number;
  resourceKey: string;
  subtext: string;
  format?: 'percentage' | 'number' | 'time';
  trend?: 'up' | 'down' | 'neutral';
  color?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  value,
  resourceKey,
  subtext,
  format = 'number',
  trend = 'neutral',
  color
}) => {
  const translate = useTranslate();

  const formatValue = () => {
    if (format === 'percentage') {
      const numValue = typeof value === 'number' ? value : parseFloat(value as string);
      return `${numValue.toFixed(1)}%`;
    }
    if (format === 'time') {
      return value;
    }
    return value?.toString() || '0';
  };

  const getTrendColor = () => {
    if (trend === 'up') return '#4caf50';
    if (trend === 'down') return '#f44336';
    return color || '#1fa2bf';
  };

  return (
    <Card sx={{
      height: '120px',
      position: 'relative',
      transition: 'all 0.2s ease-in-out',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      }
    }}>
      <CardContent sx={{ p: 2.5 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 'bold',
            color: getTrendColor(),
            fontSize: '2.25rem',
            lineHeight: 1.2,
            mb: 0.5
          }}
        >
          {formatValue()}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: '0.7rem',
            display: 'block',
            mb: 0.5
          }}
        >
          {translate(resourceKey)}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: '0.8rem'
          }}
        >
          {translate(subtext)}
        </Typography>
      </CardContent>
    </Card>
  );
};

const DashboardMetrics: React.FC<DashboardMetricsProps> = ({ data }) => {
  // Calculate values
  const occupancyRate = data.occupancyRate || 
    (data.totalLockers > 0 
      ? ((data.totalLockers - data.availableLockers) / data.totalLockers) * 100 
      : 0);

  const conversionRate = data.conversionRate || 0;
  const avgUsageHours = data.avgUsageHours || 0;
  const totalIssues = data.totalIssues || 
    (data.expiredActiveAssignments || 0) + (data.blockedLockers || 0);

  return (
    <Grid container spacing={2.5} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          value={occupancyRate}
          format="percentage"
          resourceKey="resources.dashboard.occupancy_rate"
          subtext="resources.dashboard.current_occupancy"
          trend={occupancyRate > 60 ? 'up' : occupancyRate < 30 ? 'down' : 'neutral'}
        />
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          value={data.totalAssignments || 0}
          format="number"
          resourceKey="resources.dashboard.total_assignments"
          subtext="resources.dashboard.active_count"
        />
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          value={avgUsageHours > 0 ? `${avgUsageHours.toFixed(1)}h` : '0h'}
          format="time"
          resourceKey="resources.dashboard.avg_usage_time"
          subtext="resources.dashboard.per_assignment"
        />
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          value={conversionRate}
          format="percentage"
          resourceKey="resources.dashboard.conversion_rate"
          subtext="resources.dashboard.pending_to_active"
          trend={conversionRate > 70 ? 'up' : conversionRate < 30 ? 'down' : 'neutral'}
        />
      </Grid>
    </Grid>
  );
};

export default DashboardMetrics;